import { GfgSelect } from "@/components/ui/GfgSelect";
import { FUNDING_WALLET_TEXT } from "@/constants/campaignBuilder";
import type { FundingWallet } from "@/interfaces/campaignDraft";
import { formatUsd } from "@/utils/number-format";

/**
 * The funding-wallet picker shared by the naming modal, Step 1 and Step 4. Multi-group plans only;
 * the caller decides whether to mount it.
 *
 * Credits are held per group, so the selection re-points the balance the checkout may apply AND the
 * card a top-up would charge. Both move together deliberately: a campaign funded from one group's
 * wallet and charged to another's card is the kind of split nobody notices until the invoice.
 */
export interface WalletSelectProps {
  label: string;
  wallets: FundingWallet[];
  value: number | null;
  onChange: (groupId: number) => void;
  disabled?: boolean;
  surface?: "white" | "background";
  "aria-describedby"?: string;
}

export default function WalletSelect({
  label,
  wallets,
  value,
  onChange,
  disabled = false,
  surface,
  "aria-describedby": describedBy,
}: WalletSelectProps) {
  const options = [
    { value: "", label: FUNDING_WALLET_TEXT.placeholder, disabled: true },
    ...wallets.map((w) => ({
      value: String(w.groupId),
      label: w.available === null ? w.name : `${w.name} — ${formatUsd(w.available)} available`,
    })),
  ];
  const selected = wallets.some((w) => w.groupId === value) ? String(value) : "";

  return (
    <GfgSelect
      label={label}
      options={options}
      value={selected}
      surface={surface}
      disabled={disabled || wallets.length === 0}
      aria-describedby={describedBy}
      onChange={(e) => {
        if (e.target.value) onChange(Number(e.target.value));
      }}
    />
  );
}
